import { create } from 'zustand'
import type { Session } from '@supabase/supabase-js'
import { supa } from '../lib/supa'
import { useEditor } from './useEditor'
import { consumeHandoff } from '../lib/session'
import { setCanEdit } from '../lib/editable'
import { forgetAccount, setAccountId } from '../lib/account'

/**
 * WHO IS USING THIS, AND ARE THEY ALLOWED TO.
 *
 * Two different questions with two different answers, and collapsing them is
 * how a paying customer gets shown a login form or a lapsed one gets a free
 * season. SIGNED IN comes from Supabase Auth. ALLOWED comes from the access
 * view, which the marketing site owns and writes from Stripe; this app only
 * ever reads it.
 *
 * A LAPSED PASS IS NOT A LOCKED DOOR. Everything a person ever drew stays
 * readable: they sign in, open any job, read every customer and every quote.
 * They just cannot make anything new. A season of work is why somebody comes
 * back next October, and their customers' details are theirs.
 *
 * FAILING CLOSED WOULD BE WRONG HERE, and that is a deliberate choice worth
 * defending. If the access check itself fails - the network dropped, the view
 * is briefly unavailable - a signed in person keeps working. The alternative
 * locks out a paying contractor standing in a driveway because of a blip on
 * our side, and the cost of the other error is that somebody whose card
 * bounced gets an extra hour. One of those loses a customer.
 *
 * Being signed in is NOT treated that way. No session, no app.
 */

export type AccessState = 'unknown' | 'trial' | 'paid' | 'expired'

interface AuthState {
  /** Null until the first check finishes, so nothing renders on a guess. */
  session: Session | null
  ready: boolean
  /** False only when the view actually said so. See the note above. */
  allowed: boolean
  access: AccessState
  trialEndsAt: string | null
  /**
   * The date a paid pass runs through, straight off the access view. Null
   * until it is read, and null for an account that has never paid. Billing
   * shows this or it shows nothing: a renewal date is not something to
   * calculate from today.
   */
  paidThrough: string | null

  boot: () => Promise<void>
  signIn: (email: string, password: string) => Promise<string | null>
  /**
   * SIGN IN WITH A LINK INSTEAD OF A PASSWORD.
   *
   * Asked for as: remove the password so I can log in any time without
   * needing one. Removing the door is not what that wants - a session is
   * what stamps the account id onto this browser, and every saved job is
   * keyed to it, so an app with no sign in is an app with no jobs in it.
   *
   * A link does what was actually asked. Nothing to remember, nothing to
   * reset, and the account still exists. Supabase mails a one-time link;
   * following it opens a session exactly as a password would.
   */
  sendLink: (email: string) => Promise<string | null>
  signOut: () => Promise<void>
  refreshAccess: () => Promise<void>
}

export const useAuth = create<AuthState>((set, get) => ({
  session: null,
  ready: false,
  allowed: true,
  access: 'unknown',
  trialEndsAt: null,
  paidThrough: null,

  boot: async () => {
    /*
     * Everything the address bar carried is spent first, in one pass, so a
     * person arriving from the account page is signed in before anybody asks
     * whether they are - and so that nothing else destroys the hash on the way
     * past. See lib/session.ts.
     */
    const arrived = await consumeHandoff()
    if (arrived.code) useEditor.getState().setAccessCode(arrived.code)

    const { data } = await supa.auth.getSession()
    if (data.session) setAccountId(data.session.user.id)
    set({ session: data.session, ready: true })
    if (data.session) await get().refreshAccess()

    /*
     * Keep following it. A token refresh, a sign out in another tab, an
     * expired session: all of them arrive here, and a store that only looked
     * once would go on drawing the app for somebody who is no longer signed
     * in.
     */
    supa.auth.onAuthStateChange((_event, session) => {
      if (session) setAccountId(session.user.id)
      set({ session })
      if (session) void get().refreshAccess()
      else {
        setCanEdit(true)
        set({ access: 'unknown', allowed: true, trialEndsAt: null, paidThrough: null })
      }
    })
  },

  refreshAccess: async () => {
    try {
      const { data, error } = await supa
        .from('access')
        .select('active, state, trial_ends_at, paid_through')
        .single()
      if (error || !data) return // see the note about failing open
      const allowed = !!data.active
      // The editor reads this rather than the store, because the guard has to
      // sit on the way into the document and not in a component.
      setCanEdit(allowed)
      set({
        allowed,
        access: (data.state as AccessState) ?? 'unknown',
        trialEndsAt: (data.trial_ends_at as string | null) ?? null,
        paidThrough: (data.paid_through as string | null) ?? null,
      })
    } catch {
      /* the network, not the customer. Leave them working. */
    }
  },

  signIn: async (email, password) => {
    const { error } = await supa.auth.signInWithPassword({
      email: email.trim(),
      password,
    })
    if (!error) return null
    /*
     * One message for a wrong password and for an address that has no account,
     * because saying which is which tells a stranger whose email is registered.
     */
    if (/invalid login credentials/i.test(error.message)) {
      return 'That email and password do not match an account.'
    }
    if (/email not confirmed/i.test(error.message)) {
      return 'Check your email and confirm the address first.'
    }
    return error.message
  },

  sendLink: async (email) => {
    const address = email.trim()
    if (!address) return 'Type the email your account is under.'
    const { error } = await supa.auth.signInWithOtp({
      email: address,
      options: {
        /*
         * Existing accounts only. Creating one from here would put a second
         * place accounts are made, and signing up lives on the site with the
         * trial and the card. See the note at the top of ui/SignIn.tsx.
         */
        shouldCreateUser: false,
        emailRedirectTo: window.location.origin,
      },
    })
    if (!error) return null
    /*
     * Same rule as the password path: one message whether the address has an
     * account or not, because telling those apart tells a stranger who is
     * registered. A rate limit is worth saying out loud, because waiting is
     * the actual instruction.
     */
    if (/rate limit|too many/i.test(error.message)) {
      return 'That is a lot of links. Wait a minute and ask again.'
    }
    return error.message
  },

  signOut: async () => {
    await supa.auth.signOut()
    setCanEdit(true)
    /*
     * AND THE BROWSER FORGETS WHOSE IT WAS.
     *
     * Nothing is deleted - the jobs are still in this browser and still on the
     * server - but the account stamp goes, so the next person to open this
     * machine is filtered back to their own and does not inherit a stranger's
     * customer list. Two people sharing a laptop in a shop is the ordinary
     * case, not the edge one.
     */
    forgetAccount()
    set({ session: null, access: 'unknown', allowed: true, trialEndsAt: null, paidThrough: null })
  },
}))
